import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { EntityType, NarrativeType } from '@/lib/types';
import { useEffect, useMemo, useState } from 'react';

export function useEntity<T extends { id: string }>(
  entityType: EntityType,
  id: string | undefined
) {
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!(novel?.id && id));
  const [error, setError] = useState<Error | null>(null);

  const layoutMode = novel?.storageLayout?.entities?.[entityType]?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id || !id) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.entities.subscribeEntity<T>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      entityType,
      id,
      (item) => {
        setData(item);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode, entityType, id]);

  return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error]);
}

export function useNarrativeItem<T extends { id: string }>(
  narrativeType: NarrativeType,
  id: string | undefined
) {
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!(novel?.id && id));
  const [error, setError] = useState<Error | null>(null);

  const layoutMode = novel?.storageLayout?.narrative?.[narrativeType]?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id || !id) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.narrative.subscribeNarrativeItem<T>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      narrativeType,
      id,
      (item) => {
        setData(item);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode, narrativeType, id]);

  return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error]);
}
